'use client'

import { createContext, useContext, useEffect } from 'react'

import pathConfiguration from 'src/configuration/path'
import { Dialog } from '~/core/ui/Dialog'

import { useUSearchStore } from '~/lib/features/universal-search/hooks/useUSearchStore'
import { useRouterContext } from '~/lib/next/use-router-context'

import { USearchView } from './USearchView'

export const CONSTANT_DIALOG_CUSTOM_CLASS = 'dialog-custom-class' as string
const PAGES_LIMIT_USEARCH = [
  pathConfiguration?.authError,
  pathConfiguration?.careers?.list,
  pathConfiguration?.contactUs,
  pathConfiguration?.error404,
  pathConfiguration?.error406,
  pathConfiguration?.errorAccessDenied,
  pathConfiguration?.extension?.list,
  pathConfiguration?.login,
  pathConfiguration?.termsOfUse,
  pathConfiguration?.register,
  pathConfiguration?.onboarding,
  pathConfiguration?.verifyEmail,
  pathConfiguration?.tenantBlocked,
  pathConfiguration?.privacyPolicy,
  pathConfiguration?.selectCompany,
  pathConfiguration?.careerHub.main,
  pathConfiguration?.career.list,
  pathConfiguration?.welcome
]
interface SearchContextType {
  isSearchOpen: boolean
  openSearch: () => void
  closeSearch: () => void
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

export function SearchProvider() {
  const { pathnameWithoutLocale } = useRouterContext()
  const isSearchOpen = useUSearchStore((state) => state.isSearchOpen)
  const setIsSearchOpen = useUSearchStore((state) => state.setIsSearchOpen)
  const openSearch = () => setIsSearchOpen(true)
  const closeSearch = () => setIsSearchOpen(false)

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const modifier = isMac ? e.metaKey : e.ctrlKey
      const checkDialogShowing = document.querySelector(`.${CONSTANT_DIALOG_CUSTOM_CLASS}`)
      if (
        !checkDialogShowing &&
        !PAGES_LIMIT_USEARCH.some((url) => pathnameWithoutLocale?.startsWith(url))
      ) {
        if (modifier && e.key === 'k') {
          e.preventDefault()
          openSearch()
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [pathnameWithoutLocale])

  return (
    <SearchContext.Provider value={{ isSearchOpen, openSearch, closeSearch }}>
      <Dialog
        customClickOutSide={() => closeSearch()}
        childrenWrapperClassName="!p-0"
        headingClassName="hidden"
        className="min-w-[680px] !p-0"
        open={isSearchOpen}
        label={''}
        onOpenChange={() => setIsSearchOpen(false)}>
        <USearchView onClose={closeSearch} />
      </Dialog>
    </SearchContext.Provider>
  )
}

export function useSearch() {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}
